import React from "react";
import { Col } from "react-bootstrap";
import {
  type FieldError,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  get,
} from "react-hook-form";
import TooltipTrigger from "@/components/shared/TooltipTrigger.tsx";
import { FiHelpCircle } from "react-icons/fi";

type RadioFieldRowProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  name: TName;
  labelText: string;
  errors: FieldErrors<TFieldValues>;
  tooltip?: string;
  tooltipMap?: Map<string, string>;
  labelCol?: number;
  inputCol?: number;
  isRequired?: boolean;
  children: (name: TName) => React.JSX.Element;
};

export default function RadioFieldRow<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(props: RadioFieldRowProps<TFieldValues, TName>) {
  const {
    name,
    labelText,
    errors,
    tooltip,
    tooltipMap,
    labelCol,
    inputCol,
    isRequired,
    children,
  } = props;

  const fieldError = get(errors, name) as FieldError | undefined;

  return (
    <RadioFieldRowInternal
      name={name}
      labelText={labelText}
      fieldError={fieldError}
      tooltip={tooltip}
      tooltipMap={tooltipMap}
      labelCol={labelCol}
      inputCol={inputCol}
      isRequired={isRequired}
    >
      {children}
    </RadioFieldRowInternal>
  );
}

type ValidationProps = {
  className?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
};

type RadioFieldRowInternalProps<TName extends string = string> = {
  name: TName;
  labelText: string;
  fieldError?: FieldError;
  tooltip?: string;
  tooltipMap?: Map<string, string>;
  labelCol?: number;
  inputCol?: number;
  isRequired?: boolean;
  children: (name: TName) => React.JSX.Element;
};

function RadioFieldRowInternal<TName extends string = string>({
  name,
  labelText,
  fieldError,
  tooltip,
  tooltipMap,
  labelCol = 4,
  inputCol = 6,
  isRequired,
  children,
}: RadioFieldRowInternalProps<TName>) {
  const hasError = !!fieldError;
  const tooltipText = tooltip ?? tooltipMap?.get(name);
  const tooltipId = tooltipText ? `${name}-description` : undefined;
  let childElement = children(name);

  if (React.isValidElement<ValidationProps>(childElement)) {
    const existingClassName = childElement.props.className ?? "";
    const existingDescribedBy = childElement.props["aria-describedby"];
    childElement = React.cloneElement<ValidationProps>(childElement, {
      className: `${existingClassName} ${hasError ? "is-invalid" : ""}`.trim(),
      "aria-invalid":
        hasError || childElement.props["aria-invalid"] || undefined,
      "aria-describedby":
        [existingDescribedBy, tooltipId].filter(Boolean).join(" ") ||
        undefined,
    });
  }

  const legendId = `${name}-legend`;

  return (
    <fieldset
      className="form-group row"
      role="radiogroup"
      aria-required={isRequired || undefined}
      aria-labelledby={legendId}
      aria-describedby={tooltipId}
    >
      <Col lg={labelCol} className="text-lg-end px-0">
        <legend
          id={legendId}
          className="col-form-label"
          style={{
            display: "inline-block",
            float: "none",
            width: "auto",
            margin: 0,
            border: 0,
          }}
        >
          {labelText}
          {isRequired && (
            <>
              <span
                className="required-indicator"
                aria-hidden="true"
                style={{ color: "#cc0000", marginLeft: 4 }}
              >
                *
              </span>
              <span className="visually-hidden"> (required)</span>
            </>
          )}
          {tooltipText && (
            <>
              <TooltipTrigger tooltipText={tooltipText}>
                <FiHelpCircle
                  size={16}
                  className="text-muted"
                  style={{ marginLeft: 6, cursor: "pointer" }}
                  aria-hidden="true"
                />
              </TooltipTrigger>
              <span id={tooltipId} className="visually-hidden">
                {tooltipText}
              </span>
            </>
          )}
        </legend>
      </Col>
      <Col lg={inputCol} className="px-0">
        {childElement}
        {fieldError && (
          <p role="alert" className="form-error">
            {fieldError.message}
          </p>
        )}
      </Col>
    </fieldset>
  );
}
